import _ from 'lodash';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {MessageTypesEnum} from '../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {LinkedConversationStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {getHasConversationSynopsis} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {center} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {getIsAiSynopsisEnabled} from '../../../../../store/data/company/companySelectors';
import {
  makeGetConversationChannels,
  makeGetConversationHasAiCompanion,
} from '../../../../../store/data/conversations/conversationsSelectors';
import {ConversationSummarySectionsEnum} from '../conversationSummary/conversationSummarySections';
import {ConversationHeaderAiSimilarButton} from './aiCompanion/conversationHeaderAiSimilarButton';
import {ConversationHeaderSubject} from './conversationHeaderSubject';
import {ConversationLinkedConversationsButton} from './conversationLinkedConversationsButton';
import {ConversationPinnedActivitiesButton} from './conversationPinnedActivitiesButton';
import {ConversationSimilarInquiriesButton} from './conversationSimilarInquiriesButton';
import {ConversationChannelIcon} from './conversationSubjectIcon';

/*
 * Props.
 */

interface ConversationSubjectWrapperProps {
  cellsContext: CellsContext;
  conversation: ConversationModel;
  onSubjectChange?: (arg: string) => void;
  panelId: ViewerPanelId;
  isUserVisitor: boolean;
}

/*
 * Style.
 */

const StyledSubjectContainerDiv = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: visible;
  margin-left: 8px;
  padding-right: 0;
  margin-left: -7px;
  margin-right: -7px;
  height: 34px;
`;

const StyledConversationPinnedActivitiesButton = styled(ConversationPinnedActivitiesButton)`
  flex-shrink: 0;
  align-self: center;

  ${center()};
`;

const StyledConversationLinkedConversationsButton = styled(ConversationLinkedConversationsButton)`
  flex-shrink: 0;
  align-self: center;

  ${center()};
`;

/*
 * Component.
 */

export const ConversationSubjectWrapper: FC<ConversationSubjectWrapperProps> = (props) => {
  const {cellsContext, conversation, panelId, onSubjectChange, isUserVisitor} = props;
  const getConversationChannels = useMemo(
    () => makeGetConversationChannels<void>(() => props.conversation),
    [props.conversation],
  );
  const conversationChannels = useAppSelector(getConversationChannels);

  const hasConversationSynopsisFF = useAppSelector(getHasConversationSynopsis);
  const isAiSynopsisEnabled = useAppSelector(getIsAiSynopsisEnabled);

  const getHasAiCompanion = useMemo(
    () => makeGetConversationHasAiCompanion<void>(() => props.conversation),
    [props.conversation],
  );

  const hasAiCompanion = useAppSelector(getHasAiCompanion);
  const firstChannel = _.first(conversationChannels);
  const maybeRenderChannelIcon = () => {
    // Bail-out if the channel is an email channel or if we don't have at least one channel.
    if (
      conversationChannels.length < 1 ||
      !firstChannel ||
      firstChannel.messageType === MessageTypesEnum.EMAIL
    ) {
      return null;
    }

    return <ConversationChannelIcon channel={conversationChannels[0]} />;
  };

  return (
    <StyledSubjectContainerDiv>
      {maybeRenderChannelIcon()}
      <ConversationHeaderSubject
        key={conversation.id}
        cellsContext={cellsContext}
        conversation={conversation}
        onSubjectChange={onSubjectChange}
      />
      {maybeRenderPinnedActivitiesButton(conversation, cellsContext, panelId, isUserVisitor)}
      {maybeRenderLinkedConversationsButton(conversation, cellsContext, panelId, isUserVisitor)}
      {maybeRenderSimilarConversationsButton(
        conversation,
        hasAiCompanion,
        cellsContext,
        panelId,
        isUserVisitor,
        hasConversationSynopsisFF && isAiSynopsisEnabled,
      )}
    </StyledSubjectContainerDiv>
  );
};

function maybeRenderPinnedActivitiesButton(
  conversation: ConversationModel,
  cellsContext: CellsContext,
  panelId: ViewerPanelId,
  isUserVisitor?: boolean,
) {
  if (isUserVisitor) {
    return null;
  }
  return (
    <StyledConversationPinnedActivitiesButton
      conversation={conversation}
      teammateId={cellsContext.teammateId}
      panelId={panelId}
      conversationSummarySection={ConversationSummarySectionsEnum.PINNED_ACTIVITIES}
    />
  );
}

function maybeRenderLinkedConversationsButton(
  conversation: ConversationModel,
  cellsContext: CellsContext,
  panelId: ViewerPanelId,
  isUserVisitor?: boolean,
) {
  if (isUserVisitor) {
    return null;
  }

  return (
    <StyledConversationLinkedConversationsButton
      conversation={conversation}
      teammateId={cellsContext.teammateId}
      panelId={panelId}
      conversationSummarySection={ConversationSummarySectionsEnum.LINKED_CONVERSATIONS}
      shouldDisableCount={conversation.linkStatus !== LinkedConversationStatusesEnum.ORIGINAL}
    />
  );
}

function maybeRenderSimilarConversationsButton(
  conversation: ConversationModel,
  hasAiCompanion: boolean,
  cellsContext: CellsContext,
  panelId: ViewerPanelId,
  isUserVisitor?: boolean,
  isAiSynopsisEnabled?: boolean,
) {
  if (isUserVisitor) {
    return null;
  }

  if (isAiSynopsisEnabled) {
    return <ConversationSimilarInquiriesButton conversationId={conversation.id} panelId={panelId} />;
  }

  if (hasAiCompanion && conversation.messageType === MessageTypesEnum.EMAIL) {
    return (
      <ConversationHeaderAiSimilarButton
        conversation={conversation}
        cellsContext={cellsContext}
        panelId={panelId}
      />
    );
  }

  return null;
}
